import {
  ACTION,
  Button,
  ButtonVariants,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  MOBILE_MODAL_CONTENT_PORTAL,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Text,
  useCallbackRef,
  useDisclosure,
  useMixpanel,
  type Allocation,
  type AllocationChange,
  type AllocationsSelectorProps,
  type ISubaccount,
} from '@talos/kyoko';
import { cloneDeep } from 'lodash-es';
import { useCallback, useState } from 'react';
import { useIsMobileLayout, useRoleAuth } from '../../../hooks';
import { CreateSubAccountModal } from '../../CreateSubAccountModal';

interface UseSubAccountCreationParams {
  /**
   * For simplicity we take in a condition here to bake into our authorization checks. If the implementer doesn't want to show
   * creation, or the user at hand isn't authorized to create sub accounts (checked internally), this hook returns undefined
   */
  showCreateNewSubAccount: boolean;
  /** The current selections. The selections in OMSSubAccounts are modeled as Allocations */
  subAccountAllocations: Allocation[];
  /** The effective on-change function for the OMSSubAccounts component */
  onAllocationsChange: (newAllocations: Allocation[], change: AllocationChange) => void;
}

/**
 * This hook just serves to isolate the logic OMSSubAccounts needs to be able to create sub accounts on the fly.
 * This is only done for increased readability.
 *
 * If Sub Account creation is disabled, this hook will return undefined.
 */
export const useSubAccountCreation = ({
  showCreateNewSubAccount,
  subAccountAllocations,
  onAllocationsChange,
}: UseSubAccountCreationParams) => {
  const mixpanel = useMixpanel();
  const isMobileLayout = useIsMobileLayout();
  const [lastTouchedSelector, setLastTouchedSelector] = useState<number | undefined>(undefined);
  const createSubAccountModal = useDisclosure();
  const dialogPortalProps = isMobileLayout ? { portalID: MOBILE_MODAL_CONTENT_PORTAL } : {};
  const { isAuthorized } = useRoleAuth();

  const handleUpdateAllocation = useCallback(
    <K extends keyof Allocation>(index: number, key: K, value: Allocation[K]) => {
      const newAllocations = cloneDeep(subAccountAllocations);
      newAllocations[index][key] = value;
      onAllocationsChange(newAllocations, { type: 'modification', index, key, value });
    },
    [onAllocationsChange, subAccountAllocations]
  );

  const handleSuccessfulSubAccCreation = useCallbackRef((newSubAccount: ISubaccount) => {
    const indexOfSelectionToReplace = lastTouchedSelector;
    if (indexOfSelectionToReplace == null) {
      return;
    }

    // Just some defensive programming, no special business logic case here or anything like that
    const indexNoLongerExists = indexOfSelectionToReplace >= subAccountAllocations.length;
    if (indexNoLongerExists) {
      return;
    }

    handleUpdateAllocation(indexOfSelectionToReplace, 'subAccount', newSubAccount.Name);
  });

  const handleCreateNewSubAccountClick = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CreateSubAccountClick, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.OMS,
    });
    createSubAccountModal.open();
  });

  const dropdownSuffix = useCallback<NonNullable<AllocationsSelectorProps['dropdownSuffix']>>(
    // Downshift keeps the dropdown open for clicks inside it, so dismiss it ourselves before the modal takes over.
    ({ closeMenu }) => (
      <HStack background="backgroundDropdownGroupHeader" py="spacing8" px="spacing12">
        <Button
          variant={ButtonVariants.Primary}
          onClick={() => {
            closeMenu();
            handleCreateNewSubAccountClick();
          }}
          width="100%"
          size={FormControlSizes.Small}
          data-testid="create-sub-account-button"
        >
          <HStack gap="spacing4">
            <Icon icon={IconName.Plus} />
            <Text>Create Sub Account</Text>
          </HStack>
        </Button>
      </HStack>
    ),
    [handleCreateNewSubAccountClick]
  );

  const allowCreation = isAuthorized(ACTION.CLONE_SUB_ACCOUNTS) || isAuthorized(ACTION.EDIT_SUB_ACCOUNTS);
  if (!allowCreation || !showCreateNewSubAccount) {
    return undefined;
  }

  const modal = (
    <CreateSubAccountModal
      {...createSubAccountModal}
      {...dialogPortalProps}
      onSuccessfulCreation={handleSuccessfulSubAccCreation}
    />
  );

  return {
    /** The modal, just to be rendered as `{modal}` */
    modal,
    /** Props to be spread onto the AllocationSelector component */
    allocationSelectorProps: {
      dropdownSuffix,
      onSelectorOpenChange: (index: number, isOpen: boolean) => setLastTouchedSelector(index),
    } satisfies Pick<AllocationsSelectorProps, 'dropdownSuffix' | 'onSelectorOpenChange'>,
  };
};
